import React from 'react';
import clsx from 'clsx';
import { ModalsKeys } from 'global';
import { DndProvider } from 'react-dnd';
import { useModal } from 'hooks/useModal';
import { Box } from '@mui/material';
import { useFormContext } from 'react-hook-form';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { useLocationActions } from 'hooks/useLocationActions';
import { batch, useDispatch, useSelector } from 'react-redux';
import { useSaleMode } from '../../../context';
import { useSubmissions } from '../../../hooks';
import { deskingActions, deskingSelectors } from '../../../model';
import { equipmentValuesNormalizerToFactValues } from '../../../constants';
import { BuilderViewType, FinanceProgramFormValues, FormSections, PanelProps, SaleStructureType } from '../../../types';
import {
  ContactSection,
  EquipmentSection,
  MenuBuilderSection,
  SaleDetailsSection,
  SendViaSection,
  StructureSection,
} from './sections';

export const PanelForm = (props: PanelProps) => {
  const { onClearState } = useLocationActions();
  const { resetField } = useFormContext<FinanceProgramFormValues>();

  const dispatch = useDispatch();

  useSubmissions({ voDynamicId: props?.vo?.dynamicsVendorOpportunityId ?? '' });
  const { isFinancing, onChangeStructureType } = useSaleMode();
  const { handleOpen } = useModal(ModalsKeys.Confirmation);

  const isBuilderSectionCollapsed = useSelector((state: any) =>
    deskingSelectors.isSectionCollapsed(state, FormSections.MenuBuilder)
  );

  const handleSectionLayout = (section: FormSections, key: 'isOpen' | 'isCollapsed', value: boolean) => {
    dispatch(deskingActions.updateSectionLayout({ section, key, value }));
  };

  const handleClearStructureAndMenuOptions = () => {
    handleOpen({
      title: 'Are you sure you want to reset the proposal builder?',
      message: 'This action will remove all configurations from the Finance Program and Menu.',
      onConfirm: () => {
        onClearState();
        resetField('financeQuote');

        isFinancing && onChangeStructureType(SaleStructureType.Finance);

        batch(() => {
          dispatch(deskingActions.toggleIsEditEquipmentStatus({ status: false }));
          isFinancing && dispatch(deskingActions.updateMenuBuilderViewType({ type: BuilderViewType.Finance }));
          dispatch(deskingActions.resetStructureAndMenuOptions());
        });
      },
    });
  };

  const handleChangeSelectedProductsConfiguration = (configField: string, nextValue: string | number) => {
    const normalizerFn = equipmentValuesNormalizerToFactValues[configField];

    dispatch(
      deskingActions.updateAllSelectedProductsConfiguration({
        data: { [configField]: normalizerFn ? normalizerFn(nextValue) : nextValue, customPrice: false },
      })
    );
  };

  return (
    <>
      <Box className="row">
        {props.customSections?.[FormSections.Contact]?.(
          handleChangeSelectedProductsConfiguration,
          props?.configurationByAccount
        ) ?? <ContactSection sectionName={FormSections.Contact} onContactChanged={props?.actions?.onContactChanged} />}

        <SaleDetailsSection
          sectionName={FormSections.SaleDetails}
          invoiceAction={props.invoiceAction}
          isSupportSaleDetailsUpdating={props.isSupportSaleDetailsUpdating}
          onClearStructureAndMenuOptions={handleClearStructureAndMenuOptions}
        />
      </Box>

      {props.customSections?.[FormSections.Equipment]?.(
        handleChangeSelectedProductsConfiguration,
        props?.configurationByAccount
      ) ?? (
        <EquipmentSection
          sectionName={FormSections.Equipment}
          refetchProposalProducts={props.refetchProposalProducts}
          formFieldsRequire={props.configurationByAccount?.formFieldsRequire ?? null}
          handleChangeSelectedProductsConfiguration={handleChangeSelectedProductsConfiguration}
          handleUpdateOpenStatus={(section, nextValue) => {
            handleSectionLayout(section, 'isOpen', nextValue);
          }}
          {...props.actions}
        />
      )}

      <Box className={clsx(isBuilderSectionCollapsed && 'row')}>
        <DndProvider backend={HTML5Backend}>
          <StructureSection
            sectionName={FormSections.Structure}
            createProductSlot={props.createProductSlot}
            formFieldsConfiguration={props.formFieldsConfiguration}
            onClearStructureAndMenuOptions={handleClearStructureAndMenuOptions}
            disableFinanceStructure={!!props.configurationByAccount?.actionRestrictions?.disableFinanceStructure}
            handleUpdateCollapse={(section, nextValue) => {
              handleSectionLayout(section, 'isCollapsed', nextValue);
            }}
          />

          <MenuBuilderSection
            sectionName={FormSections.MenuBuilder}
            createProductSlot={props.createProductSlot}
            isSupportBehaviorSetting={props?.isSupportBehaviorSetting}
            onClearStructureAndMenuOptions={handleClearStructureAndMenuOptions}
            notification={props.configurationByAccount?.notifications?.menuBuilder}
            handleUpdateCollapse={(section, nextValue) => {
              handleSectionLayout(section, 'isCollapsed', nextValue);
            }}
          />
        </DndProvider>
      </Box>

      <SendViaSection sectionName={FormSections.SendVia} ccEmailOptions={props.ccEmailOptions} />
    </>
  );
};
